import type { UsageEvent, UsageRow } from '../api/client'

/**
 * Kullanım ekranı — saf mantık.
 *
 * Soru şu: "kim ne kadar kullanıyor". Sunucu ham sayıları veriyor (kaç
 * antrenman, kaç tartı, son kayıt hangi gün); burada onlar bir insanın tek
 * bakışta okuyabileceği hâle geliyor.
 *
 * Neden ayrı dosya: bu hesaplar sessizce yanlış olabilecek türden. "3 gün
 * önce" yerine "2 gün önce" yazmak kimseyi çökertmez ama panele güveni bitirir
 * ve ekranda gözle yakalanmaz. Saf fonksiyon olunca test edilebiliyor.
 *
 * Tarihler yine 'YYYY-MM-DD' metni — takvimdeki gerekçenin aynısı.
 */

/** İki 'YYYY-MM-DD' arası gün farkı. b - a. */
export function daysBetween(a: string, b: string): number {
  return Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86400000)
}

/** 'YYYY-MM-DD' → "bugün" / "dün" / "5 gün önce" / "3 gün sonra". */
export function gunceGore(iso: string | null, bugun: string): string {
  if (!iso) return 'hiç'
  const d = daysBetween(iso, bugun)
  if (d === 0) return 'bugün'
  if (d === 1) return 'dün'
  if (d > 1) return `${d} gün önce`
  return `${-d} gün sonra`
}

export type Aktiflik = 'aktif' | 'ara_verdi' | 'sessiz' | 'hic'

/**
 * Son kayda göre durum. Eşikler haftalık ritme göre: 3 gün antrenman yapan
 * biri için 4 günlük sessizlik normal, 10 günlük sessizlik değil.
 */
export function aktiflik(lastEntry: string | null, bugun: string): Aktiflik {
  if (!lastEntry) return 'hic'
  const d = daysBetween(lastEntry, bugun)
  if (d <= 4) return 'aktif'
  if (d <= 10) return 'ara_verdi'
  return 'sessiz'
}

export const AKTIFLIK_ETIKET: Record<Aktiflik, string> = {
  aktif: 'aktif',
  ara_verdi: 'ara verdi',
  sessiz: 'sessiz',
  hic: 'hiç kullanmadı',
}

/**
 * Hedefe ilerleme yüzdesi. 115 → 88 hedefinde 107.5 kg = %27.8.
 *
 * Kilo ALMA hedefinde de aynı formül çalışıyor çünkü hem pay hem payda
 * aynı yöne bakıyor. Hedef ile başlangıç eşitse (veya biri girilmemişse)
 * yüzde tanımsız — 0 değil null döndürüyoruz ki ekran "%0" diye yanlış bir
 * kesinlik uydurmasın.
 */
export function ilerlemeYuzde(
  start: number, current: number | null, target: number,
): number | null {
  if (current === null) return null
  if (!start || !target || start === target) return null
  const pay = start - current
  const payda = start - target
  const oran = (pay / payda) * 100
  // Hedefi geçmek mümkün ama çubuk %100'de dolu kalsın; eksiye düşen (ters
  // yöne giden) durumda da çubuk boş görünsün.
  return Math.max(0, Math.min(100, Math.round(oran * 10) / 10))
}

/** Hedefe kalan kilo. Negatifse hedef geçilmiş demektir. */
export function kalanKg(current: number | null, target: number): number | null {
  if (current === null || !target) return null
  return Math.round((current - target) * 10) / 10
}

/**
 * Kullanıcı sıralaması: en aktif üstte.
 *
 * Sıralama anahtarı son 30 günün aktif gün sayısı; eşitlikte toplam
 * antrenman, o da eşitse isim. Kararlı olması önemli — her yenilemede
 * satırların yer değiştirdiği bir tablo okunmuyor.
 */
export function siralaKullanicilar(rows: readonly UsageRow[]): UsageRow[] {
  return [...rows].sort((a, b) =>
    b.activeDays30 - a.activeDays30 ||
    b.workoutsTotal - a.workoutsTotal ||
    a.name.localeCompare(b.name, 'tr'))
}

export interface FeedGroup {
  date: string
  events: UsageEvent[]
}

/**
 * Akışı güne göre gruplar. Sunucu zaten tarihe göre sıralı gönderiyor ama
 * ona güvenmiyoruz: burada da sıralıyoruz, çünkü grup mantığı "sıralı gelir"
 * varsayımına yaslanırsa sunucu bir gün sırayı değiştirdiğinde aynı tarih
 * iki ayrı başlık altında çıkar.
 */
export function grupla(events: readonly UsageEvent[]): FeedGroup[] {
  const map = new Map<string, UsageEvent[]>()
  for (const e of events) {
    const arr = map.get(e.date)
    if (arr) arr.push(e)
    else map.set(e.date, [e])
  }
  return [...map.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0))
    .map(([date, evs]) => ({ date, events: evs }))
}

export interface UsageOzet {
  kullanici: number
  aktif30: number
  antrenman30: number
  toplamAntrenman: number
}

/** Üst satırdaki toplamlar. */
export function ozet(rows: readonly UsageRow[]): UsageOzet {
  return {
    kullanici: rows.length,
    aktif30: rows.filter((r) => r.activeDays30 > 0).length,
    antrenman30: rows.reduce((s, r) => s + r.workouts30, 0),
    toplamAntrenman: rows.reduce((s, r) => s + r.workoutsTotal, 0),
  }
}

export const GOAL_ETIKET: Record<string, string> = {
  lose: 'kilo veriyor',
  maintain: 'koruyor',
  gain: 'kilo alıyor',
}
